import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import PricingSection from "@/components/PricingSection";
import { Loader2, Zap, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { meta } from "@/lib/metaPixel";

export default function Billing() {
  const { sub, startCheckout, plans, badgeForTier } = useSubscription();
  const [busyPlan, setBusyPlan] = useState(null);
  const [busyPack, setBusyPack] = useState(null);
  const [params] = useSearchParams();
  const pricingRef = useRef(null);
  const [highlightPlan, setHighlightPlan] = useState(null);

  useEffect(() => {
    // Honour ?plan= directly, or fall back to any stored intent from /pro-tools
    let target = params.get("plan");
    if (!target) {
      try { target = sessionStorage.getItem("dms_billing_intent"); } catch {}
    }
    if (!target) return;
    setHighlightPlan(target);
    try { sessionStorage.removeItem("dms_billing_intent"); } catch {}
    // Give the page a beat to render pricing cards, then scroll
    setTimeout(() => {
      pricingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      const el = document.querySelector(`[data-testid="pricing-card-${target}"]`);
      if (el) {
        el.classList.add("ring-4", "ring-[#4FD1C5]/70", "shadow-[0_18px_48px_rgba(79,209,197,0.35)]");
      }
    }, 250);
  }, [params]);

  const pickPlan = async (plan) => {
    setBusyPlan(plan);
    // Meta: DMS_PlanSelected (custom) + InitiateCheckout (standard) fire when
    // the user genuinely commits to a plan — not when pricing loads. Pull
    // real value/currency from the plans map so we never hard-code numbers.
    const planEntry = plans?.[plan] || null;
    const planName = planEntry?.name || plan;
    const value = typeof planEntry?.amount === "number" ? planEntry.amount : undefined;
    const currency = planEntry?.currency || "EUR";
    meta.planSelected(planName, value, currency);
    meta.initiateCheckout(planName, value, currency);
    try { const url = await startCheckout(plan); window.location.href = url; }
    catch { toast.error("Could not start checkout"); setBusyPlan(null); }
  };

  const buyPack = async (pack_id) => {
    setBusyPack(pack_id);
    try {
      const { data } = await api.post("/subscription/query-pack", { pack_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error("Could not start checkout"); setBusyPack(null); }
  };

  const statusLabel =
    sub?.status === "trialing" ? `Trialing · ${sub.trial_days_left} day${sub.trial_days_left === 1 ? "" : "s"} left` :
    sub?.status === "active" ? "Active" :
    sub?.status === "archived" ? "Trial expired · Dive Brain archived" : "No plan";
  const tierLabel = badgeForTier ? badgeForTier(sub?.tier) : "-";

  return (
    <div data-testid="billing-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-6xl">
      <div className="dms-label mb-3">Billing</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Plan &amp; usage</h1>
      <p className="mt-2 text-[#475569]">Manage your subscription and Strategy Credits.</p>

      <div className="mt-6 dms-card p-5 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="dms-label mb-1">Current plan</div>
            <div className="font-heading font-bold text-2xl text-[#0A2342]" data-testid="billing-tier">{tierLabel}</div>
            <div className="text-sm text-[#64748B] mt-0.5" data-testid="billing-status">{statusLabel}</div>
          </div>
          {sub?.paid_until && <div className="text-xs text-[#64748B]">Renews after {new Date(sub.paid_until).toLocaleDateString()}</div>}
        </div>
        {sub && !sub.is_unlimited_credits && (sub.credit_cap || sub.bonus_credits) ? (
          <div className="mt-5">
            <div className="flex items-center justify-between text-[12px] tracking-[0.18em] uppercase font-bold text-[#64748B]">
              <span>Strategy Credits this period</span>
              <span data-testid="billing-credits">{sub.credits_used || 0} / {(sub.credit_cap || 0) + (sub.bonus_credits || 0)}</span>
            </div>
            <div className="mt-2 h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
              <div className="h-full bg-[#4FD1C5]" style={{ width: `${Math.min(100, ((sub.credits_used || 0) / Math.max(1, (sub.credit_cap || 0) + (sub.bonus_credits || 0))) * 100)}%` }} />
            </div>
          </div>
        ) : null}
        {sub?.is_unlimited_credits && (
          <div className="mt-5 inline-flex items-center gap-1.5 dms-badge"><Zap className="w-3 h-3" /> Unlimited Strategy Credits</div>
        )}
      </div>

      <div className="mt-12" ref={pricingRef}>
        <div className="dms-label mb-3">Switch or upgrade plan</div>
        {highlightPlan === "pro_unlimited" && (
          <div className="mb-4 rounded-md border border-[#4FD1C5]/50 bg-[#4FD1C5]/10 px-4 py-3 text-[13px] text-[#0A2342]">
            <span className="font-semibold">Pro Unlimited</span> is highlighted below — the plan built around the 90-Day Strategy Operator.
          </div>
        )}
        {busyPlan && <div className="mb-3 text-sm text-[#64748B] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Opening checkout for {busyPlan}…</div>}
        <PricingSection compact currentTier={sub?.status === "active" ? sub.tier : null} onPick={pickPlan} />
      </div>
    </div>
  );
}
